
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import LoadingState from "@/components/profile/LoadingState";
import { toast } from "sonner";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, ExternalLink, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [errorDescription, setErrorDescription] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState<Record<string, any>>({});
  const [retryCount, setRetryCount] = useState(0);
  const [attemptingRecovery, setAttemptingRecovery] = useState(false);
  const [recoverySuccess, setRecoverySuccess] = useState(false);

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get the query parameters
        const queryParams = new URLSearchParams(location.search);
        const hashParams = new URLSearchParams(location.hash?.substring(1) || '');
        const type = queryParams.get('type') || hashParams.get('type');
        const errorParam = queryParams.get('error') || hashParams.get('error');
        const errorDescription = queryParams.get('error_description') || hashParams.get('error_description');
        const errorCode = queryParams.get('error_code') || hashParams.get('error_code');
        const code = queryParams.get('code');
        
        // Check the stored provider and prioritize it
        const storedProvider = sessionStorage.getItem('authProvider');
        const provider = storedProvider || queryParams.get('provider') || hashParams.get('provider') || 'unknown';
        
        // Check if Google auth was in progress
        const googleAuthTimestamp = sessionStorage.getItem('googleAuthInProgress');
        
        // Capture debug info
        const debug = {
          type,
          search: location.search,
          hash: location.hash, 
          pathname: location.pathname,
          error: errorParam,
          errorDescription,
          errorCode,
          provider,
          storedProvider,
          code: code ? 'present' : 'missing', // Don't log the actual code value
          timestamp: new Date().toISOString(),
          url: window.location.href,
          googleAuthInProgress: googleAuthTimestamp,
          retryCount
        };
        setDebugInfo(debug);
        
        // Log the parameters for debugging
        console.log('Auth callback received:', debug);

        // If this is an invitation, redirect to the invitation page
        if (type === 'invite') {
          console.log('Detected invitation callback, redirecting to invitation page');
          navigate(`/auth/invitation${location.hash || location.search}`, { replace: true });
          return;
        }

        // If this is a password reset, redirect to the reset password page
        if (type === 'recovery') {
          console.log('Detected password reset callback, redirecting to reset password page');
          navigate(`/auth/reset-password${location.hash || location.search}`, { replace: true });
          return;
        }

        // Explicitly handle Google Auth
        const isGoogleAuth = provider === 'google' || googleAuthTimestamp;
        if (isGoogleAuth) {
          console.log('Detected Google authentication callback');
        }

        // If we have a code parameter and Google auth was in progress, it's likely a Google auth callback
        if (code && isGoogleAuth) {
          console.log('Processing Google auth callback with code parameter');
        }

        // Get the session from the URL - important to call this on initial page load
        // to process any tokens in the URL hash or query parameters
        console.log('Retrieving session from callback URL...');
        const { data, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Error getting session:', sessionError);
          setError(`Authentication failed`);
          setErrorDescription(sessionError.message);
          setLoading(false);
          return;
        }
        
        console.log('Session retrieved:', data?.session ? 'Session exists' : 'No session');
        
        // If we have a session, the auth flow completed successfully
        if (data?.session) {
          // Authentication successful, session established
          console.log('Authentication successful, session established:', {
            userId: data.session.user.id,
            email: data.session.user.email,
            provider: data.session.user.app_metadata?.provider
          });
          
          // Clean up auth progress flags
          if (googleAuthTimestamp) {
            console.log('Clearing googleAuthInProgress flag');
            sessionStorage.removeItem('googleAuthInProgress');
          }
          sessionStorage.removeItem('authProvider');
          
          toast.success(`Sign in successful!`);
          navigate('/dashboard');
          return;
        }
        
        // No session yet, but handle different auth flows
        if ((type === 'signup' || type === 'signin' || type === 'magiclink' || 
             isGoogleAuth)) {
          
          // For Google auth specifically, we might need to try again
          if (isGoogleAuth) {
            if (retryCount < 3) {
              console.log(`Retrying session acquisition for Google auth (attempt ${retryCount + 1})`);
              setRetryCount(prev => prev + 1);
              
              // Short delay before retrying
              setTimeout(() => {
                handleAuthCallback();
              }, 1000);
              return;
            } else {
              console.log('Max retries reached for Google auth, showing error');
              setError('Google Authentication Failed');
              setErrorDescription('Unable to complete Google authentication after multiple attempts. Please try again or use email sign-in.');
              
              // Clear Google auth progress flags after max retries
              sessionStorage.removeItem('googleAuthInProgress');
              sessionStorage.removeItem('authProvider');
            }
          } else if (type === 'signup') {
            // Email signup flow
            toast.info('Please check your email to complete the sign-up process', {
              duration: 8000
            });
            navigate('/sign-in');
            return;
          } else {
            // Generic auth error
            setError('Authentication Error');
            setErrorDescription('Could not establish a session. Please try signing in again.');
          }
        } else if (type === 'recovery') {
          // Password reset is handled by the ResetPassword component
          navigate('/auth/reset-password');
          return;
        } else {
          // If no type specified and no session, something went wrong
          console.warn('No auth type specified and no session found');
          setError('Authentication Failed');
          setErrorDescription('Unable to complete authentication. Please try again.');
          
          // Clean up any auth progress flags
          sessionStorage.removeItem('googleAuthInProgress');
          sessionStorage.removeItem('authProvider');
        }
      } catch (err: any) {
        console.error('Error processing auth callback:', err);
        setError(`Authentication Error`);
        setErrorDescription(err.message || 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    handleAuthCallback();

    // Add a timeout to ensure we don't get stuck loading
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.log('Auth callback timeout reached, stopping loading state');
        setLoading(false);
        setError('Authentication Timeout');
        setErrorDescription('The authentication process took too long. Please try again.');
        
        // Clean up any auth progress flags
        sessionStorage.removeItem('googleAuthInProgress');
        sessionStorage.removeItem('authProvider');
      }
    }, 8000);

    return () => clearTimeout(timeoutId);
  }, [location, navigate, retryCount, attemptingRecovery]);

  // Handle retry button click
  const handleRetry = () => {
    // Clear any error state
    setError(null);
    setErrorDescription(null);
    setLoading(true);
    setAttemptingRecovery(false);
    
    // Clean up any stale auth flags
    sessionStorage.removeItem('googleAuthInProgress');
    sessionStorage.removeItem('authProvider');
    
    // Try again with the authentication
    navigate('/sign-in');
  };

  // Show loading state while processing
  if (loading) {
    return <LoadingState />;
  }

  // Show error if one occurred
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-background">
        <div className="max-w-md w-full space-y-6">
          <Alert variant="destructive" className="border-destructive/50 shadow-md">
            <AlertCircle className="h-5 w-5" />
            <AlertTitle className="text-lg">{error}</AlertTitle>
            <AlertDescription className="mt-2 text-sm">
              {errorDescription}
              
              {(error === "server_error" || errorDescription?.includes("Database error")) && (
                <div className="mt-4 p-3 bg-destructive/10 rounded text-xs">
                  <p className="font-medium">Server database issue detected</p>
                  <p className="mt-1">There might be a temporary issue with our user database. Please try signing in instead of creating a new account.</p>
                </div>
              )}
            </AlertDescription>
          </Alert>
          
          <div className="flex flex-col gap-3">
            <Button 
              onClick={handleRetry}
              className="w-full"
              variant="default"
            >
              <RefreshCcw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
            
            <Button 
              onClick={() => navigate('/')} 
              className="w-full"
              variant="outline"
            >
              Return to Home
            </Button>
          </div>
          
          {/* Debug info section */}
          <details className="mt-6 text-xs text-muted-foreground">
            <summary className="cursor-pointer hover:text-foreground transition-colors">Debug Information</summary>
            <div className="mt-2 p-2 bg-muted rounded overflow-auto max-h-60">
              <pre className="whitespace-pre-wrap break-all">{JSON.stringify(debugInfo, null, 2)}</pre>
            </div>
          </details>
        </div>
      </div>
    );
  }

  // Fallback UI while redirecting
  return <LoadingState />;
};

export default AuthCallback;
